import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Loader2, Sparkles, ArrowRight } from "lucide-react";
import api from "../lib/api";

const STEPS = [
  { key: "company_done", n: "01", title: "Set up your Company Profile", body: "Mission, vision and values: the strategic boundary.", to: "/profile#company" },
  { key: "personal_done", n: "02", title: "Set up your Personal Profile", body: "Tone, voice and sample writings, so it sounds like you.", to: "/profile#personal" },
  { key: "musing_done", n: "03", title: "Capture your first musing", body: "Type, talk or snap one raw thought from today.", to: "/musings/new" },
];

function FirstPost({ status, post }) {
  if (status === "pending") {
    return (
      <div className="mt-5 flex items-center gap-3 text-sm bg-[#0033FF]/5 border border-[#0033FF]/20 px-4 py-3" data-testid="first-post-pending">
        <Loader2 className="w-4 h-4 animate-spin text-[#0033FF]" /> Turning your first musing into a post…
      </div>
    );
  }
  if (status !== "ready" || !post) return null;
  return (
    <div className="mt-5 bg-[#0033FF] text-white p-5 sm:p-6" data-testid="first-post-ready">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase font-bold text-white/80 mb-2"><Sparkles className="w-3.5 h-3.5" /> Your first post is ready</div>
      <p className="text-sm leading-relaxed text-white/90 line-clamp-3 mb-4">{post.body.replace(/\*\*|__|^#+ /gm, "")}</p>
      <Link to={`/content/${post.id}`} data-testid="first-post-open"
        className="inline-flex items-center gap-2 bg-white text-zinc-900 px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-zinc-900 hover:text-white transition-colors">
        Review your post <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

export const SetupChecklist = () => {
  const [s, setS] = useState(null);

  useEffect(() => {
    let timer;
    const load = async () => {
      try {
        const { data } = await api.get("/onboarding");
        setS(data);
        if (data.first_post_status === "pending") timer = setTimeout(load, 4000);
      } catch {}
    };
    load();
    return () => clearTimeout(timer);
  }, []);

  if (!s || s.dismissed) return null;
  const done = STEPS.filter((st) => s[st.key]).length;
  const allDone = done === STEPS.length;
  if (allDone && s.first_post_status !== "pending" && s.first_post_status !== "ready") return null;

  const dismiss = async () => { setS({ ...s, dismissed: true }); await api.post("/onboarding/dismiss").catch(() => {}); };

  return (
    <div className="border border-zinc-200 p-5 sm:p-6 mb-8" data-testid="setup-checklist">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-[#0033FF] mb-1">Get set up</div>
          <div className="text-xl font-black tracking-tighter" data-testid="setup-progress">{allDone ? "You're all set." : `${done} of ${STEPS.length} done`}</div>
        </div>
        <button onClick={dismiss} data-testid="setup-dismiss" className="text-zinc-400 hover:text-zinc-900 p-1" aria-label="Hide checklist"><X className="w-4 h-4" /></button>
      </div>
      <div className="h-1 bg-zinc-100 mb-5"><div className="h-1 bg-[#0033FF] transition-[width] duration-700" style={{ width: `${(done / STEPS.length) * 100}%` }} /></div>
      <div className="grid md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        {STEPS.map((st) => {
          const ok = s[st.key];
          return (
            <Link key={st.key} to={st.to} data-testid={`setup-step-${st.key}`} data-done={ok}
              className={`bg-white p-4 group transition-colors ${ok ? "" : "hover:bg-zinc-50"}`}>
              <div className="flex items-center gap-3 mb-2">
                <span className={`w-6 h-6 flex items-center justify-center text-[10px] font-bold ${ok ? "bg-[#0033FF] text-white" : "border border-zinc-300 text-zinc-500 font-mono"}`}>
                  {ok ? <Check className="w-3.5 h-3.5" /> : st.n}
                </span>
                <span className={`text-sm font-bold ${ok ? "text-zinc-400 line-through" : "group-hover:text-[#0033FF]"}`}>{st.title}</span>
              </div>
              <div className="text-xs text-zinc-500 pl-9">{st.body}</div>
            </Link>
          );
        })}
      </div>
      <FirstPost status={s.first_post_status} post={s.first_post} />
    </div>
  );
};
